import { useEffect, useState, useContext } from "react";
import "./App.css";
import {
  Button,
  Card,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_ONE_VIEW,
  GET_ROW,
  GET_ROWS,
  UPDATE_ONE_ROW_MUTATION,
} from "./graphql-ops";
import PropertySearch from "./propertySearch";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import EditIcon from "@mui/icons-material/Edit";
import {
  GetHeaderText,
  ReturnVisibleColumns,
  passedColsParsed,
} from "./DatagridFunctions";
import { filterOutColumns } from "./layout";
import { getRelatedRows, TagDisplay, getTimeStampCell } from "./dataDisplay";
import { isArray } from "lodash";
import { SocketIOContext } from "./socketIOProvider";
import _ from "lodash";
import {
  DataGridPremium,
  GridActionsCellItem,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { GlobalContext } from "./globalContext";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import useUpdateRow from "./useUpdateRow";
import { RefetchContext } from "./refetchProvider";

export function splitArrayByViewId(array: any[]) {
  if (!Array.isArray(array)) {
    return [];
  }

  const groupedArrays = array?.reduce((groups, item) => {
    const viewId = item?.viewId?._id || item?.merc_viewId;
    if (!groups[viewId]) {
      groups[viewId] = [];
    }
    groups[viewId].push(item);
    return groups;
  }, {});

  return !!groupedArrays ? Object.values(groupedArrays) : [];
}

export function RelatedProperties(props: { row: any; rowId: any }) {
  const { row, rowId } = props;

  // rowProvider.processRows flattens relatedRows to an array of parsed
  // row objects (with _id, merc_viewId, merc_viewName, merc_Header, ...parsedRowObject).
  // Raw GraphQL responses (from updateRow mutations) come back as { rows, tags }
  // with stringified rowObject — handle both shapes.
  const rawRelatedRows: any[] = Array.isArray(row?.relatedRows)
    ? row.relatedRows
    : row?.relatedRows?.rows || [];

  let tags = new Map();

  try {
    const tagsString = Array.isArray(row?.relatedRows)
      ? undefined
      : row?.relatedRows?.tags;
    tags = !!tagsString ? new Map(JSON.parse(tagsString)) : new Map();
  } catch (e) {
    console.log(e);
  }

  const normalizeRelatedRow = (r: any) => {
    if (r?.rowObject) {
      try {
        return { _id: r._id, ...JSON.parse(r.rowObject) };
      } catch (e) {
        console.log(e);
      }
    }
    return r;
  };

  const [addingRelated, setAddingRelated] = useState(false);
  const viewIdToPass = row?.viewId?._id || row?.viewId || "";
  const refetchContext = useContext(RefetchContext);
  const globalContext = useContext(GlobalContext);

  const { updateRow } = useUpdateRow(viewIdToPass);

  const [selectedOptions, setSelectedOptions] = useState<any[]>(
    rawRelatedRows.map(normalizeRelatedRow)
  );

  const [newOptions, setNewOptions] = useState<any[]>([]);

  useEffect(() => {
    const sourceRows: any[] = Array.isArray(row?.relatedRows)
      ? row.relatedRows
      : row?.relatedRows?.rows || [];

    const relatedRows = sourceRows.map((r: any) => {
      const view = refetchContext?.allViews?.find((v) => {
        return v?._id === r?.viewId?._id || v?._id === r?.merc_viewId;
      });

      return {
        ...r,
        columns: view?.columns,
      };
    });

    const splitArrays = splitArrayByViewId(relatedRows);

    setNewOptions(splitArrays);

    setSelectedOptions(relatedRows.map(normalizeRelatedRow));
  }, [row?.relatedRows]);

  const rootRow = JSON.parse(row?.rowObject || "{}");
  const rootRowTitle = rootRow[Object.keys(rootRow)[0]];

  const { socket } = useContext(SocketIOContext) as any;

  useEffect(() => {
    if (
      !_.isNil(socket) &&
      !_.isNil(viewIdToPass) &&
      !_.isEmpty(viewIdToPass)
    ) {
      socket.emit("join-room", viewIdToPass);
    }
  }, [viewIdToPass, socket]);

  return (
    <div style={{ padding: "20px" }}>
      <Grid container justifyContent={"space-between"} sx={{ padding: "20px" }}>
        <Grid item xs={9}>
          <Typography
            style={{
              color: "rgb(40, 42, 48)",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Related
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Button
            style={{
              fontSize: "14px",
              color: "rgba(0, 0, 0, 0.7)",
              textTransform: "none",
              float: "right",
              border: "1px solid rgba(0, 0, 0, 0.7)",
            }}
            variant="outlined"
            startIcon={
              selectedOptions.length === 0 ? <AddIcon /> : <EditIcon />
            }
            onClick={() => {
              setAddingRelated(!addingRelated);
            }}
          >
            {selectedOptions.length === 0 ? "Add related" : "Edit related"}
          </Button>
        </Grid>
        <Grid item xs={12}>
          {addingRelated && (
            <Card
              style={{
                marginTop: "15px",
                border: "1px solid rgb(239, 241, 244)",
                boxShadow: "rgb(0 0 0 / 9%) 0px 3px 12px",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={10}>
                  <PropertySearch
                    selectedOptions={selectedOptions}
                    setSelectedOptions={setSelectedOptions}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    style={{ height: "100%", width: "100%" }}
                    startIcon={<AddIcon />}
                    onClick={async () => {
                      await updateRow({
                        variables: {
                          set: {
                            relatedRows: {
                              tags: JSON.stringify(Array.from(tags)),
                              rows: {
                                link: selectedOptions.map(
                                  (option) => option._id
                                ),
                              },
                            },
                          },
                          query: {
                            _id: rowId,
                          },
                        },
                      });

                      const relatedRows =
                        row?.relatedRows?.rows || row?.relatedRows || [];

                      const filterIdValues = relatedRows.map(
                        (item: any) => item._id
                      );

                      const newTwoWayRelationships = _.filter(
                        selectedOptions,
                        (item) => !_.includes(filterIdValues, item._id)
                      );

                      // Handle removing relationships
                      const selectedIds = selectedOptions.map(
                        (option) => option._id
                      );
                      
                      const removedRelationships = relatedRows.filter(
                        (row: any) => !selectedIds.includes(row._id)
                      );

                      // Update linked Rows for both adding and removing relationships
                      const addPromises = newTwoWayRelationships.map((r: any) => {
                        return updateRow({
                          variables: {
                            set: {
                              relatedRows: {
                                rows: {
                                  // Need to add the current rows to the link
                                  link: r?.relatedRows?.concat([rowId]) || [
                                    rowId,
                                  ],
                                },
                              },
                            },
                            query: {
                              _id: r._id,
                            },
                          },
                        });
                      });

                      // Remove bidirectional relationships
                      const removePromises = removedRelationships.map((r: any) => {
                        // Get the current row data from global context to get fresh relationship data
                        // @ts-ignore
                        const currentRowData = globalContext?.rows?.[r.merc_viewId]?.get(r._id);
                        
                        // Get current related row IDs - convert from objects to IDs
                        const currentRelatedRows = currentRowData?.relatedRows || [];
                        const currentRelatedIds = currentRelatedRows.map((relRow: any) => relRow._id);
                        
                        // Filter out only the current row ID, keeping all other relationships
                        const updatedRelatedRows = currentRelatedIds.filter(
                          (relatedId: string) => relatedId !== rowId
                        );
                        
                        return updateRow({
                          variables: {
                            set: {
                              relatedRows: {
                                rows: {
                                  link: updatedRelatedRows,
                                },
                              },
                            },
                            query: {
                              _id: r._id,
                            },
                          },
                        });
                      });

                      Promise.all([...addPromises, ...removePromises])
                        .then(() => {
                          console.log("All relationships updated successfully");
                        })
                        .catch((error) => {
                          console.error("Error updating relationships:", error);
                        });

                      setAddingRelated(false);
                    }}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Card>
          )}
        </Grid>
      </Grid>
      {selectedOptions.length === 0 && !addingRelated && (
        <>
          <ChangeCircleIcon
            fontSize="large"
            sx={{
              width: "100px",
              height: "100px",
              opacity: ".6",
              margin: "auto",
              display: "block",
              marginTop: "70px",
            }}
          />
          <Typography
            sx={{
              textAlign: "center",
              marginBottom: "70px",
              marginTop: "10px",
            }}
          >
            No related records found
          </Typography>
        </>
      )}
      {newOptions.length > 0 &&
        newOptions.map((view: any) => {
          return (
            <RenderViewTable
              key={view?.[0]?.viewId?._id || view?.[0]?.merc_viewId}
              view={view}
              row={row}
              rootRowTitle={rootRowTitle}
            />
          );
        })}
    </div>
  );
}

function RenderViewTable(props: any) {
  const { view, row, rootRowTitle } = props;

  const globalContext = useContext(GlobalContext);
  const refetchContext = useContext(RefetchContext);

  const fullView = refetchContext?.allViews?.find((v) => {
    return v?._id === view[0]?.merc_viewId || v?._id === view[0]?.viewId?._id;
  });

  let initialState = {};
  try {
    initialState = JSON.parse(fullView?.initialState || "{}");
  } catch (error) {
    console.error("Error parsing initialState:", error);
  }
  // const {
  //   loading,
  //   error,
  //   data,
  //   refetch: refetchView,
  // } = useQuery(GET_ONE_VIEW, {
  //   variables: {
  //     query: {
  //       _id: fullView?._id,
  //       isDeleted: false,
  //     },
  //   },
  // });

  const viewName = fullView?.name;
  const columns = ReturnVisibleColumns(
    filterOutColumns(fullView?.columns),
    initialState
  );

  const rows = view
    ?.map((r: any) => {
      // Get the view ID consistently
      const viewId = r?.viewId?._id || r?.merc_viewId;

      // Try to get row data from global context first
      //@ts-expect-error Just a type issue
      let rowObj = globalContext?.rows?.[viewId]?.get(r?._id);

      // If not in global context, try to parse from rowObject
      if (!rowObj && r?.rowObject) {
        try {
          rowObj = JSON.parse(r.rowObject);
        } catch (error) {
          console.error("Error parsing rowObject:", error);
        }
      }

      // If we still don't have rowObj, use the row data directly
      // (since the example shows properties are directly on the object)
      if (!rowObj) {
        rowObj = r;
      }

      // Skip rows without valid data
      if (!rowObj || !r?._id) {
        return null;
      }

      return {
        ...rowObj,
        ...r,
        merc_viewId: viewId,
      };
    })
    .filter((r: any) => r !== null);

  const apiRef = useGridApiRef();

  const parsedColumns = passedColsParsed(columns, apiRef)?.map((col: any) => {
    return {
      ...col,
      editable: false,
    };
  });
  if (!!columns) {
    return (
      <>
        <Typography
          sx={{
            marginTop: "30px",
            marginBottom: "15px",
            fontWeight: "600",
          }}
        >
          {viewName}
        </Typography>
        <DataGridPremium
          columns={[
            {
              field: "actions",
              type: "actions",
              width: 50,
              cellClassName: "actions-cell",
              hideable: false,
              getActions: (params: any) => {
                return [
                  <Link
                    // to={`row/${params.row._id}`}
                    to={`/list/${params.row?.merc_viewId}/row/${params.row._id}`}
                    onClick={() => {
                      window.scrollTo(0, 0);
                    }}
                    state={{
                      allRows: rows,
                      relatedRoot: row,
                      passedAllRows: JSON.stringify(rows),
                      relatedTitle: rootRowTitle,
                    }}
                    key={`link-${params.row._id}`}
                  >
                    <GridActionsCellItem
                      icon={
                        <OpenInFullIcon
                          id="Expand row"
                          sx={{ width: "15px" }}
                        />
                      }
                      label="Expand"
                    />
                  </Link>,
                ];
              },
            },
            ...parsedColumns,
          ]}
          rows={rows}
          apiRef={apiRef}
          autoHeight
          hideFooter={true}
          sx={{ marginBottom: "20px" }}
          getRowId={(row) => row._id}
        />
      </>
    );
  }

  return <></>;
}
